import React, { useState, useEffect, useRef } from 'react';
import { getGeminiSuggestions } from '../../Utils/apiServices';
import { getContextForGeminiLabel } from '../../Utils/geminiContext';

const GeminiAutocomplete = ({ 
  label, 
  value, 
  onChange, 
  placeholder = "Type to get suggestions...",
  className = "",
  error = false,
  multiline = false,
  rows = 3,
  maxLength,
  helperText,
  inputId,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceTimer = useRef(null);
  const suggestionsRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    // Close suggestions when clicking outside
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchSuggestions = async (input, context) => {
    // Use provided context, or generate from label + input so backend gets meaningful hints
    const effectiveContext = (context && context.trim()) ? context.trim() : getContextForGeminiLabel(label, input || '');
    if (!input && !effectiveContext) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await getGeminiSuggestions({
        label: label,
        input: input || '',
        context: effectiveContext
      });

      if (response.success && response.data) {
        // Handle different response formats from the API
        let parsedSuggestions = [];
        
        // Backend returns: { success: true, data: { suggestions: [...] } }
        // apiServices wraps it: { success: true, data: { success: true, data: { suggestions: [...] } } }
        // So we need to check response.data.data.suggestions
        
        // If response.data is directly an array
        if (Array.isArray(response.data)) {
          parsedSuggestions = response.data;
        } 
        // If response.data has a data property with suggestions (most common case)
        else if (response.data.data && response.data.data.suggestions && Array.isArray(response.data.data.suggestions)) {
          parsedSuggestions = response.data.data.suggestions;
        }
        // If response.data has a suggestions property directly
        else if (response.data.suggestions && Array.isArray(response.data.suggestions)) {
          parsedSuggestions = response.data.suggestions;
        }
        // If response.data.data is directly an array
        else if (response.data.data && Array.isArray(response.data.data)) {
          parsedSuggestions = response.data.data;
        }
        // Handle numeric keys (0, 1, 2, etc.) from response.data.data
        else if (response.data.data && typeof response.data.data === 'object' && response.data.data !== null) {
          const keys = Object.keys(response.data.data);
          const numericKeys = keys.filter(k => !isNaN(parseInt(k)) && k !== 'suggestions');
          if (numericKeys.length > 0) {
            parsedSuggestions = numericKeys
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map(k => response.data.data[k])
              .filter(s => s && typeof s === 'string');
          }
        }

        if (parsedSuggestions.length > 0) {
          setSuggestions(parsedSuggestions.filter(s => s && s.length > 0).slice(0, 5));
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
        }
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching suggestions from Gemini:', error);
      setSuggestions([]);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounce (500ms after user stops typing)
    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(newValue, ''); // context is auto-generated from label + input when empty
    }, 500);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    onChange(suggestion);
    setShowSuggestions(false);
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-2 relative">
      <div className="relative">
        {multiline ? (
          <textarea
            id={inputId}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            placeholder={placeholder}
            rows={rows}
            maxLength={maxLength}
            className={`w-full rounded-lg border ${
              error ? "border-red-400" : "border-slate-200"
            } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none`}
          />
        ) : (
          <input
            id={inputId}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            placeholder={placeholder}
            maxLength={maxLength}
            className={`w-full rounded-lg border ${
              error ? "border-red-400" : "border-slate-200"
            } bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue`}
          />
        )}
      </div>
      {helperText && (
        <p className={`text-xs text-left ${error ? 'text-red-500' : 'text-slate-500'}`}>
          {helperText}
        </p>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 top-full mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <div className="max-h-60 overflow-y-auto p-1">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeminiAutocomplete;
